import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Download, Printer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { downloadCandidatePdf, PdfParagraph } from '../../candidatePdf';

type ProgressReportPreviewProps = {
  open: boolean;
  values: Record<string, string>;
  onOpenChange: (open: boolean) => void;
};

const text = (value: unknown): string => String(value || '').trim();
const escapeHtml = (value: unknown): string =>
  text(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>');

const appearanceLabel = (value: unknown): string => {
  const normalized = text(value).toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (['appearance', 'virtual', 'virtual_appearance', 'virtual_appearence', 'in_person', 'graduation', 'graduation_appearance'].includes(normalized)) {
    return 'Appearance';
  }

  return 'Non-Appearance';
};

const reportAppearanceLabel = (values: Record<string, string>): string => {
  const condition = text(values.client_current_condition);
  const resolvedAppearance = condition === 'not_well' || condition === 'graduation_encouragement'
    ? 'appearance'
    : values.appearance;
  return appearanceLabel(resolvedAppearance);
};

export const buildProgressReportLetterHtml = (values: Record<string, string>, sealSource = `${process.env.PUBLIC_URL || ''}/lacountyseal.png`): string => {
  const sections = [
    ['Housing', values.housing],
    ['Mental Health', values.mental_health],
    ['Probation', values.probation],
    ['Substance Abuse', values.substance_abuse],
    ['Other', values.other],
  ].filter(([, value]) => text(value));

  return `<!doctype html><html><head><meta charset="utf-8"><title>Progress Report</title>
    <style>body{font-family:Arial,sans-serif;line-height:1.55;margin:36px;color:#111}.letter{max-width:850px;margin:auto}.seal{max-height:100px}.section{margin:14px 0}li{margin-bottom:10px}</style>
    </head><body><div class="letter">
    <img class="seal" src="${sealSource}" alt="LA County Seal">
    <p><strong>Date:</strong> ${escapeHtml(values.date)}</p>
    <p>${escapeHtml(values.court_house)}<br>${escapeHtml(values.court_address)}</p>
    <p><strong>${reportAppearanceLabel(values)}</strong></p>
    <p><strong>RE:</strong> ${escapeHtml(values.client_name)}<br>
    <strong>Case Number:</strong> ${escapeHtml(values.case_number)}<br>
    <strong>ICMS Provider:</strong> ${escapeHtml(values.icms_provider_name)}<br>
    <strong>DSM-5 dx:</strong> ${escapeHtml(values.icd_10_codes)}<br>
    <strong>Medication:</strong> ${escapeHtml(values.medication)}<br>
    <strong>Conditional Release Date:</strong> ${escapeHtml(values.conditional_release_date)}<br>
    <strong>Court Date:</strong> ${escapeHtml(values.court_date)}<br>
    <strong>Next requested court date:</strong> ${escapeHtml(values.next_requested_court_date)}<br>
    <strong>Defense Attorney:</strong> ${escapeHtml(values.defense_attorney)}</p>
    ${text(values.body_of_letter_introduction) ? `<p>${escapeHtml(values.body_of_letter_introduction)}</p>` : ''}
    <ul>${sections.map(([label, value]) => `<li><strong>${label}:</strong> ${escapeHtml(value)}</li>`).join('')}</ul>
    ${text(values.recommendation) ? `<p>${escapeHtml(values.recommendation)}</p>` : ''}
    <p>Respectfully,</p>
    <p><strong>Case Manager</strong><br>${escapeHtml(values.case_manager)}${text(values.case_manager) ? ', ' : ''}ICMS/ODR Scattered Site Case Manager</p>
    ${text(values.program_manager) ? `<p><strong>Program Manager</strong><br>${escapeHtml(values.program_manager)}</p>` : ''}
    <p>${escapeHtml(values.contact_information)}</p>
    </div></body></html>`;
};

export const downloadProgressReportWord = async (values: Record<string, string>) => {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/lacountyseal.png`);
  if (!response.ok) throw new Error('County seal could not be loaded.');
  const seal = await response.blob();
  const sealSource = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('County seal could not be loaded.'));
    reader.readAsDataURL(seal);
  });
  const blob = new Blob([buildProgressReportLetterHtml(values, sealSource)], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `progress_report_${text(values.cid) || 'unknown'}_${new Date().toISOString().slice(0, 10)}.doc`;
  try {
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const printProgressReport = async (values: Record<string, string>): Promise<boolean> => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(buildProgressReportLetterHtml(values));
  printWindow.document.close();
  await new Promise<void>((resolve, reject) => {
    window.setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
        resolve();
      } catch {
        reject(new Error('The report could not be printed.'));
      }
    }, 250);
  });
  return true;
};

export const downloadProgressReportPdf = async (values: Record<string, string>) => {
  const seal = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('County seal could not be loaded.'));
    image.src = `${process.env.PUBLIC_URL || ''}/lacountyseal.png`;
  });
  const paragraphs: PdfParagraph[] = [
    { text: `Date: ${text(values.date)}` }, { text: `${text(values.court_house)}\n${text(values.court_address)}` },
    { text: reportAppearanceLabel(values), heading: true },
    { text: `RE: ${text(values.client_name)}\nCase Number: ${text(values.case_number)}\nICMS Provider: ${text(values.icms_provider_name)}\nDSM-5 dx: ${text(values.icd_10_codes)}\nMedication: ${text(values.medication)}\nConditional Release Date: ${text(values.conditional_release_date)}\nCourt Date: ${text(values.court_date)}\nNext requested court date: ${text(values.next_requested_court_date)}\nDefense Attorney: ${text(values.defense_attorney)}` },
    { text: text(values.body_of_letter_introduction) },
    ...(['housing', 'mental_health', 'probation', 'substance_abuse', 'other'] as const).filter((key) => text(values[key])).map((key) => ({ text: `${key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())}: ${text(values[key])}` })),
    { text: text(values.recommendation) }, { text: 'Respectfully,' },
    { text: `Case Manager\n${text(values.case_manager)}${text(values.case_manager) ? ', ' : ''}ICMS/ODR Scattered Site Case Manager` },
    { text: text(values.program_manager) ? `Program Manager\n${text(values.program_manager)}` : '' }, { text: text(values.contact_information) },
  ];
  await downloadCandidatePdf(paragraphs, `progress_report_${text(values.cid) || 'unknown'}_${new Date().toISOString().slice(0, 10)}.pdf`, seal);
};

const RESOLVE_PREVIEW = gql`query ResolveProgressReportPreview($values: [CandidateInputFieldInput!]!) { resolveProgressReportPreview(values: $values) { cells { key value } } }`;

export const ProgressReportPreview = ({ open, values, onOpenChange }: ProgressReportPreviewProps) => {
  const { data, loading, error } = useQuery<{ resolveProgressReportPreview: { cells: Array<{ key: string; value: string | null }> } }>(RESOLVE_PREVIEW, { variables: { values: Object.entries(values).map(([key, value]) => ({ key, value })) }, skip: !open, fetchPolicy: 'network-only' });
  const [downloadError, setDownloadError] = React.useState('');
  const [downloading, setDownloading] = React.useState(false);
  const previewFailed = Boolean(error) || (!loading && open && !data?.resolveProgressReportPreview);
  const actionsDisabled = loading || previewFailed || downloading;
  const resolved = { ...values, ...Object.fromEntries((data?.resolveProgressReportPreview?.cells ?? []).map((cell) => [cell.key, cell.value ?? ''])) };
  const downloadDocument = async (format: 'word' | 'pdf') => {
    setDownloadError('');
    setDownloading(true);
    try {
      await (format === 'word' ? downloadProgressReportWord(resolved) : downloadProgressReportPdf(resolved));
    } catch {
      setDownloadError(format === 'word' ? 'The Word document could not be created.' : 'The PDF could not be created.');
    } finally {
      setDownloading(false);
    }
  };
  const printDocument = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      if (!await printProgressReport(resolved)) {
        setDownloadError('The print window could not be opened. Please allow popups and try again.');
      }
    } catch {
      setDownloadError('The report could not be printed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && downloading) return;
    onOpenChange(isOpen);
  };
  return (
  <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className="flex flex-col overflow-hidden p-0" contentWidth="80vw" contentHeight="80vh" contentMaxWidth="80vw" contentMaxHeight="80vh">
      <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
        <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">Progress Report Preview</DialogTitle>
      </DialogHeader>
      {loading && <p role="status" className="p-4">Resolving the current hearing appearance…</p>}
      {(previewFailed || downloadError) && <p role="alert" className="p-4 text-[var(--semantic-error)]">{previewFailed ? 'The progress report preview could not be loaded. Please try again.' : downloadError}</p>}
      {!loading && !previewFailed && <iframe title="Progress report preview" className="min-h-0 flex-1 bg-white" srcDoc={buildProgressReportLetterHtml(resolved)} />}
      <div className="flex justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
        <Button id="progress-preview-word" disabled={actionsDisabled} type="button" variant="outline" size="sm" onClick={() => { void downloadDocument('word'); }}><Download className="h-4 w-4" />Download Word</Button>
        <Button id="progress-preview-pdf" disabled={actionsDisabled} type="button" variant="outline" size="sm" onClick={() => { void downloadDocument('pdf'); }}><Download className="h-4 w-4" />Download PDF</Button>
        <Button id="progress-preview-print" disabled={actionsDisabled} type="button" size="sm" onClick={() => { void printDocument(); }}><Printer className="h-4 w-4" />Print / Save PDF</Button>
        <Button id="progress-preview-close" disabled={downloading} type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)}>Close</Button>
      </div>
    </DialogContent>
  </Dialog>
);
};
